import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import cx from "classnames";
import styles from "../scss/DigitalAdvice.module.scss";
import BudgetIMG from "../assets/images/illustration-budget-plan.svg";
import GoalsIMG from "../assets/images/illustration-goal-plan.svg";
import UltimatIMG from "../assets/images/illustration-ultimate-plan.svg";
import Button from "../../common/components/Forms/Button/Button";
import Dropdown from "../../common/components/Forms/Dropdown/Dropdown";
import RecommendedCoverItems from "./RecommendedCoverItems";
import {
    generateQuoteGroup,
    getRecommendedCoverChangeFrequency
} from "../api/ApiDigitalAdvice";
import { AdviceControllerContext } from "../providers/DigitalAdviceProvider";
import {
    ICoverResults,
    IInterimRecommendedCoverResults
} from "../interfaces/IDigitalAdviceData";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import Loader from "../../common/components/Loader/Loader";
import { GenerateQuoteCategory } from "../enums/GenerateQuoteCategory";
import ContactUsModal from "../../compare/components/Modals/ContactUsModal/ContactUsModal";

interface IRecommendedCover {
    type: "budget" | "goals" | "ultimate";
    isHighlight?: boolean;
    recommendedCover?: IInterimRecommendedCoverResults;
    frequency: string;
    setFrequency: (frequency: string) => void;
    hasHealthInsurance?: boolean;
}

const RecommendedCover: React.FC<IRecommendedCover> = (props) => {
    const dropdownFrequencyTypes = [
        { value: "Yearly", label: "Yearly" },
        { value: "Monthly", label: "Monthly" },
        { value: "Fortnight", label: "Fortnight" },
        { value: "Weekly", label: "Weekly" }
    ];
    const history = useHistory();
    const search = new URLSearchParams(location.search);
    const [frequencyCover, setFrequencyCover] = useState("");
    const [frequencyLoading, setFrequencyLoading] = useState(false);
    const [coverAdviceAmount, setCoverAdviceAmount] = useState(0);
    const [isSubmit, setIsSubmit] = useState(false);

    const [isOpenContactUsModal, setIsOpenContactUsModal] = useState(false);

    const { type, isHighlight, recommendedCover, hasHealthInsurance } = props;
    const { actions, selectors } = useContext(AdviceControllerContext);

    const sessionUserId = search.has("sessionUserId") && search.get("sessionUserId");
    const visitorId = search.has("visitorId") && search.get("visitorId");

    const isUltimateCover = (type: IRecommendedCover["type"]): boolean => {
        return type === "ultimate" || false;
    };

    const getTitle = (type: IRecommendedCover["type"]): string => {
        if (type === "budget") {
            return "Budget option";
        } else if (type === "goals") {
            return "Goals option";
        } else {
            return "Ultimate cover";
        }
    };

    const getDescription = (type: IRecommendedCover["type"]): string => {
        if (type === "budget") {
            return "Good if money is tight, but offers limited protection";
        } else if (type === "goals") {
            return "A good match for your insurance goals";
        } else {
            return "Comprehensive cover plan for total peace of mind";
        }
    };

    const getImageType = (type: IRecommendedCover["type"]): string => {
        if (type === "budget") {
            return BudgetIMG;
        } else if (type === "goals") {
            return GoalsIMG;
        } else {
            return UltimatIMG;
        }
    };

    const setRecommendedCoverType = (type: IRecommendedCover["type"]): string => {
        if (type === "budget") {
            return GenerateQuoteCategory.BUDGET;
        } else if (type === "goals") {
            return GenerateQuoteCategory.GOALS;
        } else {
            return GenerateQuoteCategory.ULTIMATE;
        }
    };

    const getAdviceCovers = (adult): Array<ICoverResults> => {
        const { covers } = adult;

        return covers || [];
    };

    const handleCoverFrequency = (frequency): void => {
        setFrequencyLoading(true);
        const postData = {
            sessionUserId,
            visitorId,
            isPrimaryAdult: true,
            recommendedCoverType: setRecommendedCoverType(type),
            frequency
        };

        const frequencyInfo = selectors.getFrequencyAdviceAmount(postData);
        if (frequencyInfo && frequencyInfo.adviceAmount) {
            setCoverAdviceAmount(frequencyInfo.adviceAmount);
            setFrequencyLoading(false);
            return;
        }
        getRecommendedCoverChangeFrequency(postData)
            .then((res) => {
                setCoverAdviceAmount(res.adviceAmount);
                actions.setFrequencyAdviceAmount({
                    recommendedCoverType: setRecommendedCoverType(type),
                    frequency: frequency,
                    isPrimaryAdult: true,
                    adviceAmount: res.adviceAmount
                });
            })
            .catch(() => {
                useErrorLogger();
            })
            .finally(() => setFrequencyLoading(false));
    };

    const handleComparePage = (): void => {
        setIsSubmit(true);
        generateQuoteGroup({
            sessionUserId,
            visitorId,
            frequency: props.frequency,
            category: setRecommendedCoverType(type)
        })
            .then((quoteGroupID) => {
                const stringState = JSON.stringify({
                    sessionUserId,
                    visitorId
                });
                window.localStorage.setItem("dap_recommendation", stringState);
                setIsSubmit(false);
                history.push(
                    `/results?groupId=${quoteGroupID}&da=our-advice&hasHealthInsurance=${hasHealthInsurance}`
                );
            })
            .catch(() => {
                useErrorLogger();
            })
            .finally(() => {
                setIsSubmit(false);
            });
    };

    useEffect(() => {
        if (recommendedCover && recommendedCover?.client && !coverAdviceAmount) {
            setCoverAdviceAmount(recommendedCover?.client.adviceAmount);
            actions.setFrequencyAdviceAmount({
                recommendedCoverType: setRecommendedCoverType(type),
                frequency: props.frequency,
                isPrimaryAdult: true,
                adviceAmount: recommendedCover?.client.adviceAmount
            });
        }
        if (props.frequency !== frequencyCover && coverAdviceAmount) {
            setFrequencyCover(props.frequency);
            handleCoverFrequency(props.frequency);
        }
    }, [recommendedCover, props.frequency]);

    return (
        <React.Fragment>
            <ContactUsModal
                isOpen={isOpenContactUsModal}
                onClose={() => setIsOpenContactUsModal(false)}
            />
            {recommendedCover?.client?.adviceAmount && (
                <div
                    className={cx([
                        styles["our-advice-recommend"],
                        {
                            [styles["product-highlights"]]: isHighlight
                        }
                    ])}>
                    {isHighlight && (
                        <div className={styles["highlights-title-container"]}>
                            <p className={styles["highlights-title"]}>Meets your goals</p>
                        </div>
                    )}

                    <img src={getImageType(type)} />
                    <label>{getTitle(type)}</label>
                    <span>{getDescription(type)}</span>
                    {isUltimateCover(type) &&
                    recommendedCover?.client?.hasExceededCover ? (
                        <Button
                            className={styles["insurer"]}
                            width="content"
                            component="button"
                            size="s"
                            variant="calltoaction"
                            onClick={() => {
                                setIsOpenContactUsModal(true);
                            }}
                            isSubmitting={isSubmit}
                            isDisabled={isSubmit}>
                            <span>CONTACT US</span>
                        </Button>
                    ) : (
                        <Button
                            className={styles["insurer"]}
                            width="content"
                            component="button"
                            size="s"
                            variant="calltoaction"
                            onClick={handleComparePage}
                            isSubmitting={isSubmit}
                            isDisabled={isSubmit}>
                            <span>Get this plan</span>
                        </Button>
                    )}
                    {frequencyLoading ? (
                        <Loader
                            className={styles["recommend-loader"]}
                            icon="Default"
                            size="default"
                        />
                    ) : (
                        <React.Fragment>
                            {isUltimateCover(type) &&
                            recommendedCover?.client?.hasExceededCover ? (
                                <div className={styles["recommend-tag"]}>
                                    <span>Contact us for personal advice</span>
                                </div>
                            ) : (
                                <div className={styles["recommend-tag"]}>
                                    <span>From</span>
                                    <label>{coverAdviceAmount}</label>
                                    <div className={styles["recommend-type-dropdown"]}>
                                        <span>/</span>
                                        <Dropdown
                                            id={`dropdown`}
                                            name={`dropdown`}
                                            options={dropdownFrequencyTypes}
                                            selectedOptionId={props.frequency}
                                            onChange={(e) => {
                                                props.setFrequency(e.target.value);
                                            }}
                                            variant={"paymentfreq-full"}
                                            className={styles["recommend-type"]}
                                        />
                                    </div>
                                </div>
                            )}
                        </React.Fragment>
                    )}
                    <RecommendedCoverItems
                        label={recommendedCover?.includeAdult2 ? "For you" : ""}
                        covers={getAdviceCovers(recommendedCover?.client)}
                    />
                    {!!(recommendedCover && recommendedCover.includeAdult2) && (
                        <RecommendedCoverItems
                            label={"For your partner"}
                            covers={getAdviceCovers(recommendedCover?.partner)}
                        />
                    )}
                </div>
            )}
        </React.Fragment>
    );
};

export default RecommendedCover;
